import React, { useCallback } from 'react'
import { PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js'

import useFlow from 'modules/flow/state/use-flow'
import useOrder from 'modules/order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'
import PaymentActionButton from './payment-action-button'
import useStripePaymentRequest from 'utils/functions/useStripePaymentRequest'
import { PaymentSelectionButtonProps } from 'modules/flow/components/payment-methods-step/components/payment-selection-button'
import { statesApi } from 'redux/services/states'
import { getClassySessionId } from 'modules/utils/getClassySessionId'
import { PaymentMethod } from 'models/ChargeIntent'
import useFinalizeSingleStepOrder from '../../../../order/finalize-order/use-finalize-single-step-order'
import {
  validateForms,
  saveFormsData,
} from '../../steps/single/utils/single-step-form-orchestrator'
import { trackFormSubmitEvent } from 'modules/analytics/helpers/track-form-events'
import useStatus from 'modules/status/state/use-status'
import { StatusType } from 'modules/status/state/status-types'

import useTrackUIElementEvent from 'modules/analytics/hooks/useTrackUIElementEvent'
import mParticleSdk from '@mparticle/web-sdk'
import { getCookieValue } from 'modules/utils/getCookieValue'
import { DEFAULT_THEME_COLORS } from '../../../../../css/theme'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'

/**
 * Helper function that wraps Stripe's event-driven PaymentRequest API
 * in a Promise to make it work like a synchronous flow
 */
const getPaymentMethodFromWallet = async (
  paymentRequest: PaymentRequest,
): Promise<PaymentRequestPaymentMethodEvent> => {
  return new Promise((resolve, reject) => {
    // Set up one-time event listeners
    const handlePaymentMethod = (event: PaymentRequestPaymentMethodEvent) => {
      // Clean up listeners
      paymentRequest.off('paymentmethod', handlePaymentMethod)
      paymentRequest.off('cancel', handleCancel)

      resolve(event)
    }

    const handleCancel = () => {
      // Clean up listeners
      paymentRequest.off('paymentmethod', handlePaymentMethod)
      paymentRequest.off('cancel', handleCancel)

      reject(new Error('Payment cancelled by user'))
    }

    // Register listeners
    paymentRequest.on('paymentmethod', handlePaymentMethod)
    paymentRequest.on('cancel', handleCancel)

    // Show the wallet UI
    paymentRequest.show()
  })
}

/**
 * Helper function to process and save payment data from digital wallet
 */
const savePaymentDataFromWallet = async (
  paymentMethodEvent: PaymentRequestPaymentMethodEvent,
  order: ReturnType<typeof useOrder>,
  lazyStates: ReturnType<typeof statesApi.useLazyGetStatesQuery>[0],
) => {
  const { payerName, paymentMethod } = paymentMethodEvent

  // Save payment method to store
  order.paymentDetails.setMethod(paymentMethod)

  // Extract and save contact info
  const names = payerName?.split(' ')
  let firstName = ''
  let lastName = ''

  if (names && names.length > 1) {
    firstName = names[0]
    lastName = names[names.length - 1]
  }

  const email = paymentMethod.billing_details?.email ?? ''
  const address = paymentMethod.billing_details.address

  const country = address?.country ?? ''
  let state = address?.state ?? ''

  // Handle state code conversion (same logic as before)
  if (country && state && (state.length > 3 || state.toUpperCase() != state)) {
    const { data: states } = await lazyStates({
      countryCode: country,
    })

    state = states?.find((stateData: any) => state == stateData.name)?.isoCode ?? ''
  }

  // Set contact info
  order.contact.setValue({ ...order.contact.value, email, lastName, firstName })

  // Set address info
  order.address.setValue({
    city: address?.city ?? '',
    country: address?.country ?? '',
    line1: address?.line1 ?? '',
    zipCode: address?.postal_code ?? '',
    state: state,
  })
}

const DigitalWalletsButton = ({ children, ...restProps }: PaymentSelectionButtonProps) => {
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const status = useStatus()
  const finalizeSingleStepOrder = useFinalizeSingleStepOrder()
  const trackEvent = useTrackUIElementEvent()
  const [lazyStates] = statesApi.useLazyGetStatesQuery()

  // Get the payment request (this is now just for setup, no event listeners)
  const paymentRequestPromise = useStripePaymentRequest()
  const [paymentRequest, setPaymentRequest] = React.useState<PaymentRequest | null>(null)

  React.useEffect(() => {
    paymentRequestPromise.then(setPaymentRequest)
  }, [paymentRequestPromise])

  const onClick = useCallback(async () => {
    if (!paymentRequest) return

    trackFormSubmitEvent(flow.currentStep.name)

    try {
      status.set(StatusType.SUBMITTING)

      // Step 1: Validate all forms and save data (just like credit card flow)
      if (!config.isEmbedded) {
        const validationResult = await validateForms()

        if (!validationResult.success) {
          throw new Error(validationResult.error || 'Please fill in all required fields')
        }

        saveFormsData(validationResult.results || [], order)
      }

      // Step 2: Get payment method from wallet (this replaces the event-driven pattern)
      const paymentMethodEvent = await getPaymentMethodFromWallet(paymentRequest)

      // Step 3: Process and save the payment data from wallet
      await savePaymentDataFromWallet(paymentMethodEvent, order, lazyStates)

      // Track analytics
      trackEvent('Donation Payment Details Submitted', mParticleSdk.EventType.Other)

      if (window.newrelic) {
        window.newrelic.addPageAction('payment_method_selected', {
          pgdid: getCookieValue('pgdid'),
          'classy-session-id': getClassySessionId(),
          paymentMethod: PaymentMethod.DW,
        })
      }

      // Tell the wallet we succeeded
      paymentMethodEvent.complete('success')

      // Step 4: Finalize the order (just like credit card flow)
      await finalizeSingleStepOrder()
    } catch (error) {
      console.error('Digital wallet payment error:', error)

      // If we have a payment method event, tell the wallet we failed
      if (error instanceof Error && error.message !== 'Payment cancelled by user') {
        // paymentMethodEvent?.complete('fail') // We'd need to handle this better
      }

      status.set(
        StatusType.FAILED,
        error instanceof Error ? error.message : 'An error occurred while processing your payment',
      )
    }
  }, [paymentRequest, flow, order, config, status, finalizeSingleStepOrder, trackEvent, lazyStates])

  // Show loading state if DW might be available but paymentRequest isn't ready
  if (order.stripePaymentRequest.canMakePayment && !paymentRequest) {
    return <Placeloader style={{ width: '100%', height: 54, borderRadius: 8 }} />
  }

  // Only show button when DW available and payment request ready
  const shouldShowButton = order.stripePaymentRequest.canMakePayment && paymentRequest

  return shouldShowButton ? (
    <PaymentActionButton
      type="button"
      color={DEFAULT_THEME_COLORS['--theme-color--neutral-20']}
      onClick={onClick}
      {...restProps}
    >
      {children}
    </PaymentActionButton>
  ) : (
    <></>
  )
}

export default DigitalWalletsButton
